import { useEffect, useState } from "react";
import { useIntl } from 'react-intl';
import { Col, Row } from "reactstrap";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Toast from '../../helpers/Toast';
import { AssistanceDescriptionUpdateDTO } from '../../models/Assistance/AssistanceDescriptionUpdateDTO';
import { AssistanceDTO } from '../../models/Assistance/AssistanceDTO';
import { AssistanceAuxiliaryEquipmentDTO } from '../../models/AssistanceAuxiliaryEquipments/AssistanceAuxiliaryEquipmentDTO';
import { SearchAuxiliaryEquipmentByDescriptionDTO } from '../../models/AuxiliaryEquipment/SearchAuxiliaryEquipmentByDescriptionDTO';
import { AssistanceService } from '../../services/AssistanceService';
import { AuxiliaryEquipmentService } from '../../services/AuxiliaryEquipmentService';
import ButtonComponent from '../ButtonComponent';
import InputComponent from '../InputComponent';
import MultiSelectComponent, { SelectItemsProps } from '../MultiSelectComponent';

type AssistanceDescriptionComponentType = {
  assistance: AssistanceDTO;
  isLoadingAssistance: boolean;
  setAssistance: Function;
}

export function AssistanceDescriptionComponent({ assistance, isLoadingAssistance, setAssistance }: AssistanceDescriptionComponentType) {
  const intl = useIntl();
  const assistanceService = new AssistanceService();
  const auxiliaryEquipmentsService = new AuxiliaryEquipmentService();

  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [isEditButtonLoading, setIsEditButtonLoading] = useState<boolean>(false);
  const [isActiveAuxiliaryEquipments, setIsActiveAuxiliaryEquipments] = useState<boolean>(false);
  // equipamentos auxiliares
  const [auxiliaryEquipments, setAuxiliaryEquipments] = useState<SelectItemsProps[]>([]);
  const [selectedAuxiliaryEquipments, setSelectedAuxiliaryEquipments] = useState<string>("");

  //módulos ativos
  const { availableModules } = useAvailableModulesContext();
  const [isActiveProfitabilityModule, setIsActiveProfitabilityModule] = useState<boolean>(false);



  const editAssistance = async () => {
    const selectedAuxiliaryEquipmentsArray = selectedAuxiliaryEquipments
      ?.split(',')
      .map((erpId: string) => {
        const selectedEquipment = auxiliaryEquipments?.find(
          (auxEquip) => auxEquip.value === erpId
        );
        return selectedEquipment
          ? new AssistanceAuxiliaryEquipmentDTO(erpId, selectedEquipment.label, "")
          : null;
      })
      .filter((equipment): equipment is AssistanceAuxiliaryEquipmentDTO => equipment !== null); // remover os nulos


    var data: AssistanceDescriptionUpdateDTO = {
      id: assistance.id,
      budgetSellingPrice: assistance.budgetSellingPrice ?? null,
      budgetCostPrice: assistance.budgetCostPrice ?? null,
      description: assistance.description ?? null,
      notes: assistance.notes,
      internalNotes: assistance.internalNotes ?? null,
      assistanceAuxiliaryEquipments: selectedAuxiliaryEquipmentsArray ?? [],
      workDone: assistance.workDone,
    };

    setIsEditButtonLoading(true);
    var response = await assistanceService.EditDescription(data);
    if (response.success === false) {
      Toast.Show("error", response.message);
      setIsEditButtonLoading(false);
      return;
    }

    // Após escolher nova obra, que irá alterar o cliente, não estava a atualizar os componentes, com isto vai atualizar
    setAssistance({
      ...assistance,
      projectErpId: null,
      client: {
        name: assistance.client?.name,
        id: assistance.client?.id
      },
      assistanceAuxiliaryEquipments: data.assistanceAuxiliaryEquipments,
    });

    Toast.Show("success", "Assistência editada com sucesso");
    setIsEditButtonLoading(false);
  };

  const getAuxiliaryEquipments = async (value: string) => {
    const list: SelectItemsProps[] = [];
    var data: SearchAuxiliaryEquipmentByDescriptionDTO = {
      description: value,
    };
    var response = await auxiliaryEquipmentsService.GetAuxiliaryEquipmentsByDescription(data);

    if (response.success === false) {
      Toast.Show("error", response.message);
      return;
    }

    response.obj.forEach((element) => {
      var item: SelectItemsProps = {
        value: element.erpId,
        label: `${element.description} - ${element.serialNumber}`,
      };
      list.push(item);
    });

    setAuxiliaryEquipments(list);
  };

  useEffect(() => {
    if (isActiveAuxiliaryEquipments) {
      getAuxiliaryEquipments("");
    }
  }, [isActiveAuxiliaryEquipments]);

  useEffect(() => {
    setIsComplete(!assistance.userCanEdit);

    if (assistance && assistance.assistanceAuxiliaryEquipments) {
      const localAssistanceAuxiliaryEquipments = assistance.assistanceAuxiliaryEquipments.map(element => ({
        value: element.erpId,
        label: `${element.description} - ${element.serialNumber}`,
      }));
      const assistanceAuxiliaryEquipmentsString = localAssistanceAuxiliaryEquipments.map(item => item.value).join(",");
      setSelectedAuxiliaryEquipments(assistanceAuxiliaryEquipmentsString);
    }
  }, [assistance]);

  useEffect(() => {
    if (availableModules.includes("IsActiveAuxiliaryEquipments")) {
      setIsActiveAuxiliaryEquipments(true);
    }
    if (availableModules.includes("IsActiveAuxiliaryEquipments")) {
      setIsActiveAuxiliaryEquipments(true);
    }
    if (availableModules.includes("ProfitabilitySage50cIsActiveProfitabilityModule")) {
      setIsActiveProfitabilityModule(true);
    }
  }, [availableModules]);

  return (
    <div className="pageComponent">
      {isActiveProfitabilityModule && (
        <>
          {/* Mostrar componentes quando profitabilityModuleValue for verdadeiro */}
          <Row style={{ margin: "0px", padding: "0px" }}>
            <InputComponent
              xl={4}
              lg={4}
              md={4}
              sm={4}
              xs={4}
              style={{ width: "100%" }}
              type={"number"}
              value={assistance?.budgetSellingPrice || 0}
              label={"Orçamento Venda S/ IVA"}
              readOnly={isComplete}
              showLoadingSkeleton={isLoadingAssistance || isEditButtonLoading}
              onChange={(t: number) => {
                setAssistance({ ...assistance, budgetSellingPrice: t });
              }}
            />
            <InputComponent
              xl={4}
              lg={4}
              md={4}
              sm={4}
              xs={4}
              style={{ width: "100%" }}
              type={"number"}
              value={assistance?.budgetCostPrice || 0}
              label={"Orçamento Custo S/ IVA"}
              readOnly={isComplete}
              showLoadingSkeleton={isLoadingAssistance || isEditButtonLoading}
              onChange={(t: number) => {
                setAssistance({ ...assistance, budgetCostPrice: t });
              }}
            />
          </Row>
        </>
      )}

      <Row style={{ margin: "0px", padding: "0px" }}>
        <InputComponent
          xl={12}
          lg={12}
          md={12}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"textArea"}
          value={assistance?.description}
          label={"Descrição do Serviço"}
          readOnly={isComplete}
          showLoadingSkeleton={isLoadingAssistance || isEditButtonLoading}
          onChange={(t: string) => {
            setAssistance({ ...assistance!, description: t });
          }}
        />
      </Row>

      <Row style={{ margin: "0px", padding: "0px" }}>
        <InputComponent
          xl={12}
          lg={12}
          md={12}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"textArea"}
          value={assistance?.notes}
          label={"Observações"}
          readOnly={isComplete}
          showLoadingSkeleton={isLoadingAssistance || isEditButtonLoading}
          onChange={(t: string) => {
            setAssistance({ ...assistance!, notes: t });
          }}
        />
      </Row>

      <Row style={{ margin: "0px", padding: "0px" }}>
        <InputComponent
          xl={12}
          lg={12}
          md={12}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"textArea"}
          value={assistance?.internalNotes}
          label={"Observações Internas"}
          readOnly={isComplete}
          showLoadingSkeleton={isLoadingAssistance || isEditButtonLoading}
          onChange={(t: string) => {
            setAssistance({ ...assistance!, internalNotes: t });
          }}
        />
      </Row>

      <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-end", alignContent: "flex-end" }}>
        {isActiveAuxiliaryEquipments && (
          <>
            <MultiSelectComponent
              xl={12}
              lg={12}
              md={12}
              sm={12}
              xs={12}
              style={{ width: "100%" }}
              label={"Equipamentos Auxiliares"}
              items={auxiliaryEquipments}
              setSelectedString={setSelectedAuxiliaryEquipments}
              selectString={selectedAuxiliaryEquipments}
              isDisabled={!assistance.userCanEdit || isLoadingAssistance || isEditButtonLoading}

            />
          </>
        )}
      </Row>

      <Row style={{ margin: "0px", padding: "0px" }}>
        <InputComponent
          xl={12}
          lg={12}
          md={12}
          sm={12}
          xs={12}
          style={{ width: "100%" }}
          type={"textArea"}
          value={assistance?.workDone}
          label={"Trabalho Efetuado"}
          readOnly={isComplete}
          showLoadingSkeleton={isLoadingAssistance || isEditButtonLoading}
          onChange={(t: string) => {
            setAssistance({ ...assistance!, workDone: t });
          }}
        />
      </Row>

      {isComplete ? (
        ""
      ) : (
        <Row style={{ margin: "0px", padding: "0px", justifyContent: "end" }}>
          <Col xl={3} lg={3} md={3} sm={12} xs={12}>
            <ButtonComponent
              style={{ width: "100%", marginTop: "10px" }}
              text={intl.formatMessage({ id: "AssistanceEditA.Save" })}
              onClick={() => editAssistance()}
              disabled={isLoadingAssistance || isEditButtonLoading}
            />
          </Col>
        </Row>
      )}
    </div>
  )
}
